import { SignalDot, StatusHint } from '@indipia/ui-core'
import type { SignalColor } from '@indipia/shared'
import type { ListLane, ListLaneStep } from '../../constants/scheduleListStage.logic'
import { DIM_TEXT_STYLE, SIGNAL_TEXT_COLOR } from './listStyle'

// 펼침 영역의 줄 1개(D-07) — 줄 머리(신호 점 + 줄 이름 · 상태 라벨 · 상대방 이름) + 단계 타임라인.
// 끝난 단계 = 문구, 지금 단계 = 상태 글자 + 사유(StatusHint), 이후 단계 = `예정`. notice 가 있으면 타임라인 없이 그 문구만 보인다.

const HINT_TONE: Record<SignalColor, 'error' | 'warning' | 'success' | 'info' | 'muted'> = {
  RED: 'error', YELLOW: 'warning', GREEN: 'success', BLUE: 'info', WHITE: 'muted', NONE: 'muted',
}

interface LaneStepViewProps {
  isLast: boolean
  step: ListLaneStep
  color: SignalColor
  statusLabel: string | null
  reason: string | null
}

// 스테퍼 원 — 기획서 C-6 펼침 타임라인: 16px 원 + 2px 테두리, 끝남 = 파랑 채움, 지금 = 신호색 채움 + 4px 후광, 예정 = 빈 원
const NODE = 'var(--num-16)'
const HALO: Partial<Record<SignalColor, string>> = { RED: 'var(--status-danger-100)', YELLOW: 'var(--status-warning-100)' }

function StepNode({ state, color }: { state: ListLaneStep['state']; color: SignalColor }) {
  const live = state === 'current' && (color === 'RED' || color === 'YELLOW')
  const fill = state === 'done' ? 'var(--gt-primary)' : live ? (color === 'RED' ? 'var(--signal-red)' : 'var(--signal-yellow)') : 'var(--shell-surface)'
  const edge = state === 'done' ? 'var(--gt-primary)' : live ? fill : state === 'current' ? 'var(--gt-primary)' : 'var(--gray-300)'
  return (
    <span
      aria-hidden
      style={{
        position: 'relative', zIndex: 1, display: 'block', boxSizing: 'border-box', width: NODE, height: NODE, borderRadius: '50%',
        border: 'var(--num-2) solid ' + edge, background: fill,
        boxShadow: live ? '0 0 0 var(--num-4) ' + (HALO[color] ?? 'transparent') : undefined,
      }}
    />
  )
}

function LaneStepView({ isLast, step, color, statusLabel, reason }: LaneStepViewProps) {
  return (
    <div style={{ position: 'relative', flex: '1 1 0', minWidth: 'var(--num-80)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-02)', paddingRight: 'var(--spacing-02)' }}>
      {!isLast && (
        <span
          aria-hidden
          style={{ position: 'absolute', top: 'calc(var(--num-4) + var(--num-8) - var(--num-2) / 2)', left: NODE, right: 0, height: 'var(--num-2)', background: step.state === 'done' ? 'var(--gt-primary)' : 'var(--shell-border)' }}
        />
      )}
      <div style={{ padding: 'var(--num-4) 0', display: 'flex' }} role="img" aria-label={step.title + ' ' + (step.state === 'done' ? '끝남' : step.state === 'current' ? '지금' : '예정')}>
        <StepNode state={step.state} color={color} />
      </div>
      <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: step.state === 'current' ? 700 : 500, color: step.state === 'pending' ? 'var(--shell-hint)' : 'var(--shell-on-surface)' }}>
        {step.title}
      </span>
      {step.state === 'done' && <span style={DIM_TEXT_STYLE}>{step.text}</span>}
      {step.state === 'pending' && <span style={DIM_TEXT_STYLE}>{step.text === '-' ? '예정' : `예정 · ${step.text}`}</span>}
      {step.state === 'current' && (
        <>
          {statusLabel && (
            <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: SIGNAL_TEXT_COLOR[color] }}>{statusLabel}</span>
          )}
          {reason && <StatusHint tone={HINT_TONE[color]}>{reason}</StatusHint>}
        </>
      )}
    </div>
  )
}

export interface ScheduleListLaneProps {
  lane: ListLane
}

export function ScheduleListLane({ lane }: ScheduleListLaneProps) {
  const color: SignalColor = lane.color ?? 'NONE'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-03)' }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--spacing-03)' }}>
        <SignalDot color={color} size={10} />
        <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>{lane.title}</span>
        {lane.statusLabel && (
          <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: SIGNAL_TEXT_COLOR[color] }}>{lane.statusLabel}</span>
        )}
        <span style={DIM_TEXT_STYLE}>{lane.counterpart}</span>
      </div>
      {lane.notice ? (
        <StatusHint tone="muted">{lane.notice}</StatusHint>
      ) : (
        <div style={{ display: 'flex' }}>
          {lane.steps.map((step, i) => (
            <LaneStepView key={step.title} isLast={i === lane.steps.length - 1} step={step} color={color} statusLabel={lane.statusLabel} reason={lane.reason} />
          ))}
        </div>
      )}
    </div>
  )
}
